import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators
} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDialog
} from '@angular/material';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import * as cloneDeep from 'lodash/cloneDeep';
import { Attachment, SiteModel, SiteStepperModel } from 'src/app/models/index';
import {
  CommonService,
  SiteService,
  SiteStepperService
} from 'src/app/services/index';
import { SiteUtilityService } from 'src/app/services/site-utility.service';
import {
  ActionModeEnum,
  AlertService,
  Constants,
  Moment$,
  SiteStepperEnum
} from 'src/app/shared';
import * as ObjectUtils from 'util';
@Component({
  selector: 'app-site-creation',
  templateUrl: './site-creation.component.html',
  styleUrls: ['./site-creation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: Constants.DATE_RANGES_PROVIDER_FORMAT
    }
  ]
})
export class SiteCreationComponent
  implements OnInit, AfterViewChecked, AfterViewInit
{
  @ViewChild('formDirective') formDirective: FormGroupDirective;
  formData: FormGroup;
  model: SiteModel;
  stepLabel = 'Site Creation';
  stepTitle = 'New Site Creation';
  enableNextStep = false;
  logoAttachment: Attachment = new Attachment();
  layoutAttachment: Attachment = new Attachment();
  otherAttachments = [];
  attachmentObj = { id: undefined, name: '', attachmentType: '' };
  timezones = [];
  goLiveDate = new FormControl(Moment$());
  nextFollowUpDate = new FormControl(Moment$());
  actionMode: ActionModeEnum = ActionModeEnum.Creating;
  isReadOnly = false;
  availableKPIUtilities: string[];
  max = new Date();
  currencies = Constants.CURRENCIES;
  constructor(
    private siteService: SiteService,
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    private alertService: AlertService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private siteStepperService: SiteStepperService,
    private siteUtilityService: SiteUtilityService
  ) {
    this.model = new SiteModel();
    this.formData = this.fb.group({
      siteNameCtr: [
        '',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(250),
          Validators.pattern('[a-zA-Z0-9_& ./:,-]*'),
          this.startWhitespace
        ]
      ],
      industryTypeCtr: ['', [Validators.required]],
      addressBlockNoCtr: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern('[a-zA-Z0-9_& ./:,-]*'),
          this.startWhitespace
        ]
      ],
      leadTypeCtr: [''],
      addressLocalityCtr: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern('[a-zA-Z0-9_& ./:,-]*'),
          this.startWhitespace
        ]
      ],
      leadGeneratedByCtr: [''],
      cityCtr: [
        '',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(50),
          this.startWhitespace
        ]
      ],
      // contactPersonNameCtr: ['',  [Validators.required, Validators.maxLength(250)]],
      pinCodeCtr: [
        '',
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.pattern('[a-zA-Z0-9]*'),
          this.startWhitespace
        ]
      ], // validate 6 digits later
      // contactNoCtr: ['',  [Validators.required, Validators.pattern('[0-9]{10}')]], // validate 10 digits later
      industrySegmentCtr: ['', [Validators.required]],
      // contactEmailCtr: ['',  [Validators.required, Validators.email]],
      commissionStartDateCtr: [],
      // altContactNoCtr: ['',  [Validators.pattern('[0-9]{10}')]], // validate 10 digits later
      conversionFlagCtr: [],
      executionStateCtr: ['', [Validators.required]],
      nextFollowUpDateCtr: [],
      energyCalcMethodCtr: ['', [Validators.required]],
      timezoneCtr: ['', [Validators.required]],
      featureOptedCtr: ['', [Validators.required]],
      utilityOptedCtr: ['', [Validators.required]],
      currencyCtr: ['', [Validators.required]],
      layoutNavigateFeatureCtr: [],
      uploadLayoutCtr: [],
      uploadLogoCtr: ['', []]
    });
  }
  public startWhitespace(control: FormControl) {
    if (
      (control.value as string) &&
      (control.value as string).indexOf(' ') == 0
    ) {
      return { whitespace: true };
    }
    return null;
  }
  ngOnInit() {
    this.model = new SiteModel();
    this.siteUtilityService.getAvailableKPIForProject().subscribe((data) => {
      this.availableKPIUtilities = JSON.parse(JSON.stringify(data));
    });
    this.siteStepperService.getStepperSubject().subscribe((data) => {
      if (
        data.selectedIndex === SiteStepperEnum.SiteCreation &&
        !ObjectUtils.isNullOrUndefined(data.siteModel)
      ) {
        this.actionMode = ActionModeEnum.Viewing;
        this.model = data.siteModel;
        this.enableNextStep = true;
        this.stepTitle = 'View/Edit Site';
        this.setDisabledField();
        this.isReadOnly = true;
        this.setReadonlyField(true);
        const pinCodeLengthStandard = 10;
        const contactNoLengthStandard = 10;
        this.availableKPIUtilities = this.model.availableKPIUtilities;
        if (!ObjectUtils.isNullOrUndefined(this.model.pinCode)) {
          this.model.pinCode =
            this.buildZeroPrefix(
              pinCodeLengthStandard - this.model.pinCode.length
            ) + this.model.pinCode;
        }
        if (!ObjectUtils.isNullOrUndefined(this.model.contactNo)) {
          this.model.contactNo =
            this.buildZeroPrefix(
              contactNoLengthStandard - this.model.contactNo.length
            ) + this.model.contactNo;
        }
        if (!ObjectUtils.isNullOrUndefined(this.model.altContactNo)) {
          // tslint:disable-next-line:max-line-length
          this.model.altContactNo =
            this.buildZeroPrefix(
              contactNoLengthStandard - this.model.altContactNo.length
            ) + this.model.altContactNo;
        }
        this.goLiveDate = new FormControl(
          Moment$(this.model.goLiveDate).format(
            Constants.MOMENT_DATE_FORMAT.DATE_FORMAT
          )
        );
        this.nextFollowUpDate = new FormControl(
          Moment$(this.model.nextFollowUpDate).format(
            Constants.MOMENT_DATE_FORMAT.DATE_FORMAT
          )
        );
        for (let i = 0; i < this.model.attachments.length; i++) {
          if (
            this.model.attachments[i].documentType ===
            Constants.DOCUMENT_TYPE.LOGO
          ) {
            this.logoAttachment = this.model.attachments[i];
          } else if (
            this.model.attachments[i].documentType ===
            Constants.DOCUMENT_TYPE.LAYOUT
          ) {
            this.layoutAttachment = this.model.attachments[i];
          } else if (
            this.model.attachments[i].documentType ===
            Constants.DOCUMENT_TYPE.CLICK_AND_NAVIGATE_LAYOUT
          ) {
            // this.clickAndNavigateAttachment = this.model.attachments[i];
          } else {
            this.otherAttachments.push(this.model.attachments[i]);
          }
        }
      }
    });
    this.siteService.getAvailableTimezones().subscribe((data) => {
      this.timezones = data;
    });
  }
  private setDisabledField = () => {
    this.formData.get('siteNameCtr').disable();
    this.formData.get('addressBlockNoCtr').disable();
    this.formData.get('addressLocalityCtr').disable();
    this.formData.get('cityCtr').disable();
    this.formData.get('pinCodeCtr').disable();
  };
  private setReadonlyField = (value: boolean) => {
    if (value) {
      // this.formData.get('contactPersonNameCtr').disable();
      // this.formData.get('contactNoCtr').disable();
      // this.formData.get('contactEmailCtr').disable();
      // this.formData.get('altContactNoCtr').disable();
      this.formData.get('uploadLayoutCtr').disable();
      this.formData.get('uploadLogoCtr').disable();
    } else {
      // this.formData.get('contactPersonNameCtr').enable();
      // this.formData.get('contactNoCtr').enable();
      // this.formData.get('contactEmailCtr').enable();
      // this.formData.get('altContactNoCtr').enable();
      this.formData.get('uploadLayoutCtr').enable();
      this.formData.get('uploadLogoCtr').enable();
    }
  };
  public isFieldHasError = (fieldName: string, errorCode: string): boolean => {
    return this.formData.get(fieldName).hasError(errorCode);
  };
  private buildZeroPrefix = (noOfZero: number) => {
    let result = '';
    for (let i = 0; i < noOfZero; i++) {
      result += '0';
    }
    return result;
  };
  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }
  public goTo = (event: any) => {
    event.preventDefault();
    this.siteStepperService.setSendStepper(true);
    this.siteStepperService.sendStepperSubject(
      new SiteStepperModel(SiteStepperEnum.ConnectionDetails, this.model)
    );
  };
  public reset = (event: any) => {
    event.preventDefault();
    this.otherAttachments = [];
    this.formDirective.resetForm();
    this.formData.reset();
    this.goLiveDate.setValue(Moment$());
    this.nextFollowUpDate.setValue(Moment$());
  };
  public visibleResetButton = (): boolean => {
    return this.model.siteId === undefined;
  };
  public visibleEditButton = (): boolean => {
    return this.model.siteId !== undefined;
  };
  public visibleSubmitButton = (): boolean => {
    return this.model.siteId === undefined;
  };
  public invertEdit = (event: any): boolean => {
    event.preventDefault();
    const flag = (this.isReadOnly = !this.isReadOnly);
    this.setReadonlyField(flag);
    if (flag) {
      this.actionMode = ActionModeEnum.Viewing;
    } else {
      this.actionMode = ActionModeEnum.Editing;
    }
    return flag;
  };
  public submit = () => {
    if (this.goLiveDate.valid) {
      this.model.goLiveDate = Moment$(this.goLiveDate.value).valueOf();
    }
    if (this.nextFollowUpDate.valid) {
      this.model.nextFollowUpDate = Moment$(
        this.nextFollowUpDate.value
      ).valueOf();
    }
    this.model.attachments = [];
    this.model.attachments.push(this.logoAttachment);
    this.model.attachments.push(this.layoutAttachment);
    for (let i = 0; i < this.otherAttachments.length; i++) {
      if (this.otherAttachments[i].id !== undefined) {
        this.model.attachments.push(this.otherAttachments[i]);
      }
    }
    this.model.goLiveDate = Moment$(this.goLiveDate.value).valueOf();
    this.siteService.saveSiteDetails(this.model).subscribe((response: any) => {
      console.log(response);
      if (response.response === 'DuplicacyError') {
        this.alertService.message =
          'Duplicate Site Error occurred!. Please enter Valid Industry Name / Building number';
        this.alertService.showError();
      } else if (response.response === 'notSubmitted') {
        this.alertService.message =
          'Failure in saving the data! Try again with valid data.';
        this.alertService.showError();
      } else {
        if (this.model.siteId === undefined) {
          this.siteStepperService.sendStepperSubject(
            new SiteStepperModel(SiteStepperEnum.SiteCreation, this.model)
          );
          this.alertService.message = `New Site ${response.response} is created successfully!`;
        } else {
          this.alertService.message = `Site ${this.model.siteId} is updated successfully!`;
        }
        this.alertService.showSuccess();
        this.model.siteId = response.response;
        this.setDisabledField();
        this.enableNextStep = true;
      }
    });
  };
  public uploadDocument = (event: any, pictType: string, index: number = 0) => {
    if (event.target.files.length <= 0) {
      return;
    }
    const files = event.target.files;
    if (!this.commonService.isImageFile(files[0])) {
      this.commonService.showDialogMessage('File type not supported!');
      return;
    }
    if (event.target.files[0].size > 1024000 && pictType == 'Logo') {
      this.alertService.showError('File is too big!');
      return;
    }
    if (event.target.files[0].size > 2048000) {
      if (pictType == 'Other') {
        this.alertService.showError('File is too big!');
        this.otherAttachments.splice(index, 1);
      } else {
        this.alertService.showError('File is too big!');
      }
      return;
    } else {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('documentType', pictType);
      this.siteService
        .uploadDocument(formData, pictType, 'comment', this.model.siteId)
        .subscribe((data) => {
          if (data === '' || this.commonService.isNullOrUndefined(data)) {
            this.alertService.showError('Upload file fail!');
            return;
          }
          if (data.documentType === Constants.DOCUMENT_TYPE.LOGO) {
            this.logoAttachment = data;
          } else if (data.documentType === Constants.DOCUMENT_TYPE.LAYOUT) {
            this.layoutAttachment = data;
          } else if (data.documentType === Constants.DOCUMENT_TYPE.OTHER) {
            this.otherAttachments[index] = data;
          }
          for (let i = 0; i < this.model.attachments.length; i++) {
            if (data.documentType === this.model.attachments[i].documentType) {
              this.model.attachments.splice(i, 1);
              break;
            }
          }
        });
    }
  };
  public removeDocument = (index: number) => {
    this.otherAttachments.splice(index, 1);
  };
  public addMoreDocument = () => {
    const temp = cloneDeep(this.attachmentObj);
    this.otherAttachments.push(temp);
  };
  public selectDocument = (elmId: string) => {
    const element: HTMLElement = document.getElementById(elmId) as HTMLElement;
    element.click();
  };
}
